import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useIntl } from 'react-intl';

import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';

import Form, { FormTextField } from '@ts-core/core/components/FormV2';

import { ChangePasswordInput } from '../../types/Auth';

interface ChangePasswordFormProps {
  onSubmit: (data: ChangePasswordInput, onSuccess: () => void) => void;

  isSubmitting?: boolean;
  submitError?: string;
}

const ChangePasswordFormSchema = Yup.object({
  currentPassword: Yup.string().required('Ingresa tu contraseña actual'),
  password: Yup.string().required('Ingresa la nueva contraseña'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Las contraseñas no coinciden')
    .required('Repite la nueva contraseña'),
});

const ChangePasswordForm = (props: ChangePasswordFormProps) => {
  const { onSubmit, isSubmitting, submitError } = props;
  const intl = useIntl();

  const { handleSubmit, formState, control, reset } = useForm<ChangePasswordInput>({
    resolver: yupResolver(ChangePasswordFormSchema),
  });

  const handleChangePassword = useCallback(
    (data: ChangePasswordInput) => {
      onSubmit(data, () => {
        reset();
      });
    },
    [onSubmit, reset]
  );

  return (
    <Box boxShadow={1}>
      <Box height={48} display="flex" flexDirection="row" alignItems="center" p={2}>
        <Typography variant="subtitle2" gutterBottom>
          {intl.formatMessage({ id: 'profile.passwordSection' })}
        </Typography>
      </Box>
      <Divider />
      <Form
        sx={{ width: '100%', p: 2 }}
        onSubmit={handleSubmit(handleChangePassword)}
        isSubmitting={isSubmitting}
        submitError={submitError}
        formState={formState}
      >
        <Stack spacing={2}>
          <FormTextField
            control={control}
            name="currentPassword"
            type="password"
            rules={{ required: true }}
            fullWidth
            label={intl.formatMessage({ id: 'field.currentPassword.label' })}
            defaultValue=""
          />
          <FormTextField
            control={control}
            name="password"
            rules={{ required: true }}
            defaultValue=""
            type="password"
            fullWidth
            label={intl.formatMessage({ id: 'field.password.label' })}
          />
          <FormTextField
            control={control}
            name="confirmPassword"
            rules={{ required: true }}
            defaultValue=""
            type="password"
            fullWidth
            label={intl.formatMessage({ id: 'field.confirmPassword.label' })}
          />
        </Stack>
      </Form>
    </Box>
  );
};

export default ChangePasswordForm;
